import { useMemo } from 'react';
import { MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { useQueries } from 'react-query';
import { ScheduleApiQueryKeys } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { SchemaScheduleService } from '@frontend/schema';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';

export const useGetIsAppointmentTypeMappedWithPractitioner = (isEnabled: boolean) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { selectedLocationIds } = useSchedulingLocationInfo();

  const listMappingsQueriesV3 = SchedulerV3.Queries.useListMappingsQuery({
    request: {
      locationIds: selectedLocationIds,
      mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
      showInactive: false,
    },
    options: {
      enabled: isScheduleV3FlagEnabled && !!selectedLocationIds.length && isEnabled,
      refetchOnMount: 'always',
    },
    bypassScheduleV3FlagCheck: true,
  });

  const appointmentTypesQueriesV2 = useQueries(
    selectedLocationIds.map((locationId) => ({
      queryKey: ScheduleApiQueryKeys.scheduleBookingSiteQueryKeys.appointmentTypes({ locationId }),
      queryFn: () => SchemaScheduleService.GetAppointmentTypeList({ locationId }),
      enabled: !isScheduleV3FlagEnabled && !!locationId && isEnabled,
      refetchOnMount: 'always' as const,
    }))
  );

  const locationWiseMappingStatus = useMemo<Record<string, boolean>>(() => {
    if (isScheduleV3FlagEnabled) {
      return selectedLocationIds.reduce<Record<string, boolean>>((acc, locationId) => {
        acc[locationId] = (listMappingsQueriesV3.data?.mappings ?? []).some(
          (mapping) =>
            mapping.locationId === locationId && mapping.appointmentTypeMetaData?.id && mapping.providerMetaData?.id
        );
        return acc;
      }, {});
    }

    return appointmentTypesQueriesV2.reduce<Record<string, boolean>>((acc, query) => {
      const currentLocationId = query.data?.data?.[0]?.locationId;
      if (currentLocationId) {
        const hasMappedProviders = !!query.data?.data?.some((appointmentType) =>
          appointmentType.providerAssets?.some((provider) => !!provider.available)
        );
        acc[currentLocationId] = hasMappedProviders;
      }
      return acc;
    }, {});
  }, [isScheduleV3FlagEnabled, appointmentTypesQueriesV2, selectedLocationIds, listMappingsQueriesV3.data?.mappings]);

  // Check if there is a mapping for any of the location
  const hasAnyLocationMapping = Object.values(locationWiseMappingStatus).includes(true);
  const isLoading = appointmentTypesQueriesV2.some((query) => query.isLoading) || listMappingsQueriesV3.isLoading;

  return {
    isLoading,
    hasAnyLocationMapping,
    locationWiseMappingStatus,
  };
};
